export function formatOrdinalDateEn(d) {
  if (!d) return '';
  const day = d.getDate();
  const k = day % 100;
  const suf =
    k > 10 && k < 14
      ? 'th'
      : day % 10 === 1
        ? 'st'
        : day % 10 === 2
          ? 'nd'
          : day % 10 === 3
            ? 'rd'
            : 'th';
  const mon = d.toLocaleDateString('en-US', { month: 'short' });
  return `${day}${suf} ${mon}, ${d.getFullYear()}`;
}

/** Search-strip date label (e.g. 12, Sep'24 mobile / 12 Sep'26 desktop). */
export function formatStripDate(d, useComma = false) {
  if (!d) return '';
  const day = d.getDate();
  const mon = d.toLocaleDateString('en-US', { month: 'short' });
  const yy = String(d.getFullYear()).slice(-2);
  return useComma ? `${day}, ${mon}'${yy}` : `${day} ${mon}'${yy}`;
}

/** Compact date for mobile Travellers sheet footer (e.g. 24th Jan'26). */
export function formatShortTripDate(d) {
  if (!d) return '';
  const day = d.getDate();
  const k = day % 100;
  const suf =
    k > 10 && k < 14
      ? 'th'
      : day % 10 === 1
        ? 'st'
        : day % 10 === 2
          ? 'nd'
          : day % 10 === 3
            ? 'rd'
            : 'th';
  const mon = d.toLocaleDateString('en-US', { month: 'short' });
  const yy = String(d.getFullYear()).slice(-2);
  return `${day}${suf} ${mon}'${yy}`;
}

export function deduplicateAirportsModule(airports) {
  const seen = new Set();
  return airports.filter((airport) => {
    if (seen.has(airport.iataCode)) return false;
    seen.add(airport.iataCode);
    return true;
  });
}

export function buildAirportFilterResult(options, excludeIataCode) {
  let filteredOptions = options;
  if (excludeIataCode) {
    filteredOptions = options.filter((option) => option.value !== excludeIataCode);
  }

  const directAirports = filteredOptions.filter((o) => o.isDirectMatch === true);
  const nearbyAirports = filteredOptions
    .filter((o) => o.isDirectMatch === false && o.distanceKm && o.distanceKm <= 200)
    .sort((a, b) => a.distanceKm - b.distanceKm);

  // If no direct match exists, promote the first result as main
  if (directAirports.length === 0 && filteredOptions.length > 0) {
    const promoted = { ...filteredOptions[0], isDirectMatch: true };
    directAirports.push(promoted);
    const promotedCode = promoted.iataCode;
    const idx = nearbyAirports.findIndex((a) => a.iataCode === promotedCode);
    if (idx !== -1) nearbyAirports.splice(idx, 1);
  }

  const directAirportCodes = new Set(directAirports.map((a) => a.iataCode));
  const filteredNearby = nearbyAirports.filter((a) => !directAirportCodes.has(a.iataCode));

  const result = [];
  const airportGroups = new Map();
  directAirports.forEach((airport) => {
    airportGroups.set(airport.iataCode, { main: airport, nearby: [] });
  });
  filteredNearby.forEach((airport) => {
    const mainCode = airport.isNearestTo;
    if (airportGroups.has(mainCode)) {
      airportGroups.get(mainCode).nearby.push(airport);
    } else {
      airportGroups.set(mainCode, { main: null, nearby: [airport] });
    }
  });

  airportGroups.forEach((group) => {
    if (group.main) result.push(group.main);
    if (group.nearby.length > 0) {
      result.push({
        label: `${group.nearby.length} Nearby Airport${group.nearby.length > 1 ? 's' : ''} found | within 200 KMs`,
        isSeparator: true,
        iataCode: 'SEPARATOR',
        name: 'separator',
      });
      group.nearby.forEach((nearest) => {
        result.push({
          ...nearest,
          label: `  └─ ${nearest.name} (${nearest.iataCode}) - ${nearest.address?.cityName || nearest.cityName}, ${nearest.address?.countryName || nearest.countryName}`,
          distanceLabel: `📍 ${nearest.distanceKm} km from ${nearest.isNearestTo || 'main airport'}`,
          isNearestAirport: true,
          mainAirportCode: nearest.isNearestTo,
          mainAirportName: nearest.name,
          'data-nearest': 'true',
          style: {
            backgroundColor: '#f8f9fa',
            color: '#6c757d',
            fontSize: '0.6rem',
            paddingLeft: '20px',
            borderLeft: '2px solid #dee2e6',
          },
        });
      });
    }
  });

  return result.length > 0 ? result : filteredOptions;
}

export function formatCalendarPriceModule(price) {
  if (price >= 100000) return `₹${(price / 100000).toFixed(1)}L`;
  if (price >= 1000) return `₹${(price / 1000).toFixed(1)}k`;
  return `₹${price}`;
}

/** City + IATA for multicity row display (Figma two-tone desktop; IATA-first mobile). */
export function parseMcAirportDisplay(airport, rawInput) {
  if (airport) {
    const city = airport.cityName || airport.address?.cityName || '';
    const iata = airport.iataCode || airport.value || '';
    return { city, iata };
  }
  if (!rawInput || typeof rawInput !== 'string') return { city: '', iata: '' };
  const trimmed = rawInput.trim();
  const m = trimmed.match(/^(.*?)\s+([A-Z]{3})$/);
  if (m) return { city: m[1].trim(), iata: m[2] };
  const paren = trimmed.match(/\(([A-Z]{3})\)/);
  if (paren) {
    return {
      city: trimmed.replace(/\s*\([A-Z]{3}\)\s*$/, '').trim(),
      iata: paren[1],
    };
  }
  return { city: trimmed, iata: '' };
}

/** Mobile strip display when an airport is selected (e.g. Delhi, India (DEL)). */
export function formatAirportStripDisplay(airport) {
  if (!airport) return '';
  const city = airport.cityName || airport.address?.cityName || '';
  const country = airport.countryName || airport.address?.countryName || 'India';
  const iata = airport.iataCode || airport.value || '';
  if (city && iata) return `${city}, ${country} (${iata})`;
  if (iata) return iata;
  return city;
}

/** Mobile airport sheet — Figma 6544:98010 selected row: "BLR - Bengaluru" + airport name. */
export function formatMobileSheetSelectedAirport(airport) {
  if (!airport) return null;
  const city = airport.cityName || airport.address?.cityName || '';
  const iata = airport.iataCode || airport.value || '';
  const airportName = airport.name || '';
  if (!iata && !city) return null;
  return {
    codeCity: iata && city ? `${iata} - ${city}` : (iata || city),
    airportName,
  };
}
